import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { createKitchen } from "../../../redux/actions/kitchenActions";
import { toast } from "react-hot-toast";
import moment from "moment";
import { useEffect } from "react";
import { useLanguage } from "../../../context/LanguageContext";

const SectionAGeneralInformation = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useLanguage();

  const { state } = useLocation();

  // Extract canteen data from state
  const canteen = state?.canteen || {};
  const surveyDetail = state?.surveyDetail || {};
  const surveyId = state?.surveyId;
  const newSurvey = state?.newSurvey;

  const [form, setForm] = useState({
    location: canteen?.kitchen_location_fulladdress || canteen?.address || "",
    a1: "",
    a1Remarks: "",
    a2: "",
    a2Remarks: "",
    a3: "",
    a4From: "06:00",
    a4To: "18:00",
    a5: "",
  });

  const [errors, setErrors] = useState({});
  const [latLng, setLatLng] = useState({
    lat: null,
    lng: null,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error(t.global.alert_geolocation_unsupported);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;

        setLatLng({
          lat: latitude.toFixed(6),
          lng: longitude.toFixed(6),
        });
      },
      (error) => {
        toast.error(t.global.alert_geolocation_unsupported);
        console.error(error);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
      },
    );
  };

  useEffect(() => {
    fetchCurrentLocation();
  }, []);

  // Load existing survey data if available
  useEffect(() => {
    if (surveyDetail?.gen_data) {
      const genData = surveyDetail.gen_data;
      setForm((prev) => ({
        ...prev,
        location: genData.kitchen_location_fulladdress || prev.location,
        a1: genData.is_gen_info_survey_done === 1 ? t.global.yes : "",
        a1Remarks: genData.gen_info_A1_remark || "",
        a2:
          genData.gen_info_A2 === 1
            ? t.global.shgs
            : genData.gen_info_A2 === 2
              ? t.global.others
              : "",
        a2Remarks: genData.gen_info_A2_remark || "",
        a3: genData.gen_info_A3 || "",
        a4From: genData.gen_info_A4?.slice(0, 5) || "12:00",
        a4To: genData.gen_info_A4_to?.slice(0, 5) || "15:00",
        a5: genData.gen_info_A5 || "",
      }));
    }
  }, [surveyDetail, t]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;

    setForm((prev) => ({ ...prev, [name]: value }));

    // Clear error when user makes a selection or inputs data
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required field validations
    if (!form.location.trim()) newErrors.location = t.global.err_address;
    if (!form.a1) newErrors.a1 = t.global.err_a1;
    if (form.a1 === t.global.no && !form.a1Remarks.trim()) {
      newErrors.a1Remarks = t.global.err_a1;
    }
    if (!form.a2) newErrors.a2 = t.global.err_a2;
    if (form.a2 === t.global.others && !form.a2Remarks.trim()) {
      newErrors.a2Remarks = t.global.err_a2;
    }
    if (!form.a3) newErrors.a3 = t.global.err_a3;
    if (!form.a4From) newErrors.a4From = t.global.err_a4;
    if (!form.a4To) newErrors.a4To = t.global.err_a4;
    if (!form.a5) newErrors.a5 = t.global.err_a5;

    // Numeric validation
    if (form.a3 && (isNaN(form.a3) || form.a3 < 0 || form.a3 > 999)) {
      newErrors.a3 = t.global.err_a3;
    }
    if (form.a5 && (isNaN(form.a5) || form.a5 < 0 || form.a5 > 999999)) {
      newErrors.a5 = t.global.err_a5;
    }

    // Time validation
    if (form.a4From && form.a4To) {
      const fromTime = new Date(`2000-01-01T${form.a4From}`);
      const toTime = new Date(`2000-01-01T${form.a4To}`);
      if (fromTime >= toTime) {
        newErrors.a4To = t.global.err_a4_order;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      toast.error(t.global.alert_fix_errors);
      return;
    }

    if (!latLng.lat || !latLng.lng) {
      toast.error(t.global.alert_missing_ids);
      return;
    }

    setIsSubmitting(true);

    try {
      const fd = new FormData();

      // FIXED: Get location IDs from canteen object
      const districtId = canteen.district_id || state?.district_id;
      const municipalityId = canteen.municipality_id || state?.municipality_id;
      const wardId = canteen.ward_id || state?.ward_id;
      const areaId = canteen.id || state?.area_id;
      const canteenTypeId =
        canteen.canteen_type_id ||
        canteen.canteen_type ||
        state?.canteen_type_id;

      console.log("Saving with location IDs:", {
        district_id: districtId,
        municipality_id: municipalityId,
        ward_id: wardId,
        area_id: areaId,
        canteen_type_id: canteenTypeId,
      });

      // LOCATION DATA
      fd.append("district_id", districtId);
      fd.append("municipality_id", municipalityId);
      fd.append("ward_id", wardId);
      fd.append("area_id", areaId);
      fd.append("canteen_type_id", canteenTypeId);

      // OPTIONAL PARENT KITCHEN
      if (canteen?.parent_kitchen?.id) {
        fd.append("parent_kitchen_id", canteen.parent_kitchen.id);
      }

      // ADDRESS
      fd.append("kitchen_location_fulladdress", form.location);

      // LAT / LNG
      fd.append("latitude", latLng.lat);
      fd.append("longitude", latLng.lng);

      // GENERAL INFO
      fd.append("gen_info_A1", form.a1 === t.global.yes ? "1" : "2");
      fd.append("gen_info_A2", form.a2 === t.global.shgs ? "1" : "2");
      fd.append("gen_info_A1_remark", form.a1Remarks || "");
      fd.append("gen_info_A2_remark", form.a2Remarks || "");
      fd.append("gen_info_A3", form.a3);
      fd.append("gen_info_A4", form.a4From || "");
      fd.append("gen_info_A4_to", form.a4To || "");
      fd.append("gen_info_A5", form.a5);
      fd.append("current_section", "geninfo");

      // META
      fd.append("reporting_date", moment().format("YYYY-MM-DD"));

      // If this is an existing survey, include survey ID
      if (surveyId) {
        fd.append("survey_id", surveyId);
      }

      // API CALL
      await dispatch(createKitchen(fd));

      // Update completedSections in localStorage
      const completedSections = JSON.parse(
        localStorage.getItem("completedSections") || "{}",
      );
      completedSections.a = true;
      localStorage.setItem(
        "completedSections",
        JSON.stringify(completedSections),
      );

      toast.success(t.global.success_message);

      // Navigate back to sections page with updated data
      navigate("/survey/sections", {
        state: {
          canteen: canteen,
          surveyDetail: surveyDetail,
          surveyId: surveyId,
          newSurvey: newSurvey,
        },
      });
    } catch (error) {
      console.error("Error saving Section A:", error);
      toast.error(t.global.alert_server_error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-xl shadow p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">
                {t.global.sectionA_title}
              </h2>
              <p className="text-gray-600">{t.global.sectionA_subtitle}</p>
            </div>
            <div className="text-sm bg-sky-100 text-sky-800 px-3 py-1 rounded-full">
              {canteen?.area_name ||
                `${t.global.canteen_id_label} ${canteen?.id || t.global.na}`}
            </div>
          </div>

          {/* Display location IDs for debugging (optional - remove in production) */}
          {/* <div className="mb-4 p-3 bg-gray-50 rounded-lg text-xs text-gray-600">
            <p className="font-medium">{t.global.location_info}</p>
            <p>{t.global.district_id}: {canteen.district_id || t.global.na} | {t.global.municipality_id}: {canteen.municipality_id || t.global.na} | {t.global.ward_id}: {canteen.ward_id || t.global.na}</p>
            <p>{t.global.canteen_id_label}: {canteen.id} | {t.global.canteen_type_id}: {canteen.canteen_type_id || canteen.canteen_type}</p>
          </div> */}

          <div className="space-y-6">
            {/* Location */}
            <div className="border rounded-lg p-4">
              <label className="block font-medium mb-2">
                {t.modules.kitchen.sections.A.questions.address}{" "}
                <span className="text-red-500">*</span>
              </label>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {t.shared.sectionA.questions.address}{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="location"
                    value={form.location}
                    placeholder={t.global.location_placeholder}
                    className={`w-full border p-2 rounded ${errors.location ? "border-red-500" : ""}`}
                    rows="3"
                    onChange={handleChange}
                    required
                  />
                  {errors.location && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.location}
                    </p>
                  )}
                </div>

                {/* Location Coordinates Display */}
                <div className="bg-gray-50 p-3 rounded">
                  <p className="text-sm font-medium">
                    {t.global.location_info}
                  </p>
                  <p className="text-sm text-gray-600">
                    {latLng.lat && latLng.lng
                      ? `Latitude: ${latLng.lat}, Longitude: ${latLng.lng}`
                      : t.global.loading}
                  </p>
                  <button
                    type="button"
                    onClick={fetchCurrentLocation}
                    className="mt-2 text-sm text-blue-600 hover:text-blue-800"
                  >
                    {t.global.refresh_location || "Refresh Location"}
                  </button>
                </div>
              </div>
            </div>

            {/* A1 - Kitchen Functionality */}
            <div className="border rounded-lg p-4">
              <label className="block font-medium mb-2">
                A1. {t.modules.kitchen.sections.A.questions.functional}{" "}
                <span className="text-red-500">*</span>
              </label>
              <div className="flex space-x-6 mb-3">
                {[t.global.yes, t.global.no].map((option) => (
                  <label key={option} className="inline-flex items-center">
                    <input
                      type="radio"
                      name="a1"
                      value={option}
                      checked={form.a1 === option}
                      onChange={handleChange}
                      className="mr-2 h-4 w-4"
                      required
                    />
                    <span className="font-medium">{option}</span>
                  </label>
                ))}
              </div>
              {errors.a1 && (
                <p className="text-red-500 text-sm mb-2">{errors.a1}</p>
              )}

              {form.a1 === t.global.no && (
                <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <label className="block text-sm font-medium mb-2">
                    {t.global.remarks_label}{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="a1Remarks"
                    placeholder={t.global.remarks_functional_placeholder}
                    className={`w-full border p-2 rounded ${errors.a1Remarks ? "border-red-500" : ""}`}
                    rows="3"
                    onChange={handleChange}
                    value={form.a1Remarks}
                    required
                  />
                  {errors.a1Remarks && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.a1Remarks}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* A2 - Kitchen Operation */}
            <div className="border rounded-lg p-4">
              <label className="block font-medium mb-2">
                A2. {t.modules.kitchen.sections.A.questions.operated}{" "}
                <span className="text-red-500">*</span>
              </label>
              <div className="flex space-x-6 mb-3">
                {[t.global.shgs, t.global.others].map((option) => (
                  <label key={option} className="inline-flex items-center">
                    <input
                      type="radio"
                      name="a2"
                      value={option}
                      checked={form.a2 === option}
                      onChange={handleChange}
                      className="mr-2 h-4 w-4"
                      required
                    />
                    <span className="font-medium">{option}</span>
                  </label>
                ))}
              </div>
              {errors.a2 && (
                <p className="text-red-500 text-sm mb-2">{errors.a2}</p>
              )}

              {form.a2 === t.global.others && (
                <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <label className="block text-sm font-medium mb-2">
                    {t.global.remarks_label}{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="a2Remarks"
                    placeholder={t.global.remarks_operated_placeholder}
                    className={`w-full border p-2 rounded ${errors.a2Remarks ? "border-red-500" : ""}`}
                    rows="3"
                    onChange={handleChange}
                    value={form.a2Remarks}
                    required
                  />
                  {errors.a2Remarks && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.a2Remarks}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* A3 - Cook Cum Helpers */}
            <div className="border rounded-lg p-4">
              <label className="block font-medium mb-2">
                A3. {t.modules.kitchen.sections.A.questions.staff}{" "}
                <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="a3"
                value={form.a3}
                placeholder={t.global.staff_placeholder}
                className={`w-full border p-3 rounded ${errors.a3 ? "border-red-500" : ""}`}
                onChange={handleChange}
                min="0"
                max="999"
                required
              />
              {errors.a3 && (
                <p className="text-red-500 text-sm mt-1">{errors.a3}</p>
              )}
              <p className="text-sm text-gray-500 mt-1">
                {t.modules.kitchen.sections.A.hints.staff}
              </p>
            </div>

            {/* A4 - Timeline */}
            <div className="border rounded-lg p-4">
              <label className="block font-medium mb-2">
                A4. {t.modules.kitchen.sections.A.questions.timeline}{" "}
                <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    {t.global.timing_from}{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="time"
                    name="a4From"
                    value={form.a4From}
                    onChange={handleChange}
                    className={`w-full border p-3 rounded ${errors.a4From ? "border-red-500" : ""}`}
                    required
                  />
                  {errors.a4From && (
                    <p className="text-red-500 text-sm mt-1">{errors.a4From}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    {t.global.timing_to} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="time"
                    name="a4To"
                    value={form.a4To}
                    onChange={handleChange}
                    className={`w-full border p-3 rounded ${errors.a4To ? "border-red-500" : ""}`}
                    required
                  />
                  {errors.a4To && (
                    <p className="text-red-500 text-sm mt-1">{errors.a4To}</p>
                  )}
                </div>
              </div>
              <div className="mt-4 p-3 bg-blue-50 rounded">
                <p className="text-sm font-medium text-blue-800">
                  {t.modules.kitchen.sections.A.hints.timeline}:{" "}
                  <span className="font-bold">
                    {form.a4From} {t.global.timing_to_word} {form.a4To}
                  </span>
                </p>
              </div>
            </div>

            {/* A5 - Average Beneficiaries */}
            <div className="border rounded-lg p-4">
              <label className="block font-medium mb-2">
                A5. {t.modules.kitchen.sections.A.questions.beneficiaries}{" "}
                <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="a5"
                value={form.a5}
                placeholder={t.global.beneficiaries_placeholder}
                className={`w-full border p-3 rounded ${errors.a5 ? "border-red-500" : ""}`}
                onChange={handleChange}
                min="0"
                required
              />
              {errors.a5 && (
                <p className="text-red-500 text-sm mt-1">{errors.a5}</p>
              )}
              <p className="text-sm text-gray-500 mt-1">
                {t.global.beneficiaries_placeholder}
              </p>
            </div>

            {/* Required fields note */}
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-yellow-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-yellow-700">
                    <span className="text-red-500">*</span>{" "}
                    {t.global.err_complete_all}
                  </p>
                  <p className="text-xs text-yellow-600 mt-1">
                    {t.modules.kitchen.sections.A.remarksNote ||
                      'Note: Remarks are required when selecting "NO" for A1 or "Others" for A2'}
                  </p>
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex justify-between pt-6 border-t">
              <button
                onClick={() =>
                  navigate("/survey/sections", {
                    state: {
                      canteen: canteen,
                      surveyDetail: surveyDetail,
                      surveyId: surveyId,
                      newSurvey: newSurvey,
                    },
                  })
                }
                className="px-6 py-3 bg-gray-200 rounded-lg hover:bg-gray-300 transition font-medium"
                disabled={isSubmitting}
              >
                ← {t.global.back_to_sections}
              </button>
              <div className="flex space-x-4">
                <button
                  onClick={handleSave}
                  className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? t.global.submitting
                    : t.global.submit_section_a}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SectionAGeneralInformation;
